import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, SlidersHorizontal } from "lucide-react";
import api from "../api";
import { useT } from "../i18n";
import Header from "../components/Header";
import ProCard from "../components/ProCard";
import FilterPanel from "../components/FilterPanel";

const HERO_IMG = "/pub_facebook.jpg";

export default function HomePage() {
  const { t } = useT();
  const [meta, setMeta] = useState({ specialties: [], communes: [] });
  const [pros, setPros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [commune, setCommune] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [availability, setAvailability] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    api.get("/meta").then((res) => setMeta(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (commune) params.commune = commune;
    if (specialty) params.specialty = specialty;
    if (availability) params.availability = availability;
    api.get("/pros", { params })
      .then((res) => setPros(res.data.pros))
      .catch(() => setPros([]))
      .finally(() => setLoading(false));
  }, [search, commune, specialty, availability]);

  const hasFilters = search || commune || specialty || availability;
  const clearFilters = () => { setSearch(""); setCommune(""); setSpecialty(""); setAvailability(""); };

  return (
    <div className="min-h-screen">
      <Header />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_IMG})` }} aria-hidden="true" />
        <div className="absolute inset-0 bg-[#FDFBF7]/85" aria-hidden="true" />
        <div className="relative max-w-6xl mx-auto px-4 md:px-8 py-14 md:py-20">
          <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#0D4045] mb-3">{t("app.tagline")}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#1F2937] max-w-3xl" style={{ fontFamily: "Manrope" }}>
            {t("hero.title")}
          </h1>
          <p className="text-base md:text-lg text-[#6B7280] mt-4 max-w-2xl">{t("hero.subtitle")}</p>

          <div className="mt-8 flex gap-2 max-w-2xl">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B7280]" />
              <input
                data-testid="search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("search.placeholder")}
                className="w-full min-h-[52px] rounded-full border border-[#0D4045]/15 bg-white pl-11 pr-4 text-base shadow-[0_8px_30px_rgba(13,64,69,0.06)] focus:outline-none focus:ring-2 focus:ring-[#0D4045]/30"
              />
            </div>
            <button data-testid="filters-toggle-btn" onClick={() => setFiltersOpen(!filtersOpen)}
              className={`flex items-center gap-2 min-h-[52px] px-5 rounded-full font-semibold text-sm border transition-colors duration-200 md:hidden ${
                filtersOpen ? "bg-[#0D4045] text-[#FDFBF7] border-[#0D4045]" : "bg-white text-[#0D4045] border-[#0D4045]/15"
              }`}>
              <SlidersHorizontal size={16} /> {t("filters.button")}
            </button>
          </div>
          {meta.has_examples && (
            <p data-testid="prototype-banner" className="mt-4 text-xs text-[#92400E] bg-[#FEF3C7] border border-[#FBBF24]/60 rounded-full px-4 py-2 inline-block">
              {t("prototype.banner")}
            </p>
          )}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        <div className="md:grid md:grid-cols-[260px_1fr] md:gap-10">
          <aside className={`${filtersOpen ? "block" : "hidden"} md:block mb-8 md:mb-0`}>
            <div className="md:sticky md:top-24 bg-white rounded-xl border border-black/5 p-6 flex flex-col gap-6">
              <FilterPanel meta={meta} commune={commune} setCommune={setCommune}
                availability={availability} setAvailability={setAvailability}
                specialty={specialty} setSpecialty={setSpecialty}
                hasFilters={hasFilters} onClear={clearFilters} />
            </div>
          </aside>

          <main>
            <p data-testid="results-count" className="text-sm font-semibold text-[#6B7280] mb-4">
              {pros.length === 1 ? t("results.count.one") : t("results.count.other", { n: pros.length })}
            </p>
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => (
                  <div key={`skeleton-${i}`} className="bg-white rounded-xl border border-black/5 h-64 animate-pulse" />
                ))}
              </div>
            ) : pros.length === 0 ? (
              <p data-testid="results-empty" className="text-base text-[#6B7280] bg-white rounded-xl border border-black/5 p-8">
                {t("results.empty")}
              </p>
            ) : (
              <div data-testid="pro-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {pros.map((pro, i) => <ProCard key={pro.id} pro={pro} index={i} />)}
              </div>
            )}
          </main>
        </div>
      </section>

      <footer className="border-t border-[#0D4045]/10 mt-8">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
          <p className="text-xs text-[#6B7280] max-w-2xl">{t("footer.privacy")}</p>
          <Link to="/confidentialite" data-testid="footer-legal-link"
            className="inline-block mt-3 text-xs font-semibold text-[#0D4045] underline underline-offset-2 hover:text-[#0a3237] transition-colors duration-200">
            {t("footer.legal")}
          </Link>
          <p className="text-xs text-[#6B7280] mt-2">© 2026 Lantouraj — Guadeloupe</p>
        </div>
      </footer>
    </div>
  );
}
